import { AlertListRow } from '@fullstory/proto/fs/services/dx-objects';
import { int64 } from '@fullstory/proto/google/protobuf';
import { createErrorReducer, createLoadingReducer } from '@fullstory/redux-util';
import { combineReducers } from 'redux';
import { MetricAlertsRequestActions } from './actions';
import { MetricAlertsByMetric, MetricAlertsListMetadata, MetricAlertsRequestTypes } from './types';

const initialState: MetricAlertsListMetadata = {
  atAlertLimit: false,
  orgLimit: int64.fromNumber(0),
  alertCount: int64.fromNumber(0),
};

const metadataReducer = (
  state: MetricAlertsListMetadata = initialState,
  action: MetricAlertsRequestActions,
): MetricAlertsListMetadata => {
  switch (action.type) {
    case MetricAlertsRequestTypes.GetAlertsListViewSuccess: {
      const { rows, ...rest } = action.data;
      return rest;
    }
  }
  return state;
};

// only store alerts here when request is made for entire org
const activeAlertsReducer = (
  state: AlertListRow[] = [],
  action: MetricAlertsRequestActions,
): AlertListRow[] => {
  switch (action.type) {
    case MetricAlertsRequestTypes.GetAlertsListViewSuccess: {
      if (action.request.body?.archived || action.request.body?.dxIdVersion) return state;
      return action.data.rows;
    }
    case MetricAlertsRequestTypes.ArchiveAlertConfigRequest: {
      const alertConfigId = action.body?.alertConfigId;
      if (!alertConfigId) return state;
      return state.filter(row => row.alertId !== alertConfigId);
    }
  }
  return state;
};

// only store alerts here when request is made for entire org
const archivedAlertsReducer = (
  state: AlertListRow[] = [],
  action: MetricAlertsRequestActions,
): AlertListRow[] => {
  switch (action.type) {
    case MetricAlertsRequestTypes.GetAlertsListViewSuccess: {
      if (!action.request.body?.archived || action.request.body?.dxIdVersion) return state;
      return action.data.rows;
    }
    case MetricAlertsRequestTypes.UnarchiveAlertConfigRequest: {
      const alertConfigId = action.body?.alertConfigId;
      if (!alertConfigId) return state;
      return state.filter(row => row.alertId !== alertConfigId);
    }
  }
  return state;
};

const byMetricReducer = (
  state: MetricAlertsByMetric = {},
  action: MetricAlertsRequestActions,
): MetricAlertsByMetric => {
  switch (action.type) {
    case MetricAlertsRequestTypes.GetAlertsListViewSuccess: {
      const metricId = action.request.body?.dxIdVersion?.id;
      if (action.request.body?.archived) return state;

      // if GetAlertsListView was requested without a metric id, we can update
      // the state for all alerts byMetric
      if (!metricId) {
        return action.data.rows.reduce<MetricAlertsByMetric>(
          (acc, curr) => ({
            ...acc,
            [curr.metricId]: [...(acc[curr.metricId] ?? []), curr],
          }),
          {},
        );
      }

      // replace all the alerts stored on this metric with the response from the
      // API but keep the other metric's alerts around
      return { ...state, [metricId]: action.data.rows };
    }
  }
  return state;
};

const loadingReducer = createLoadingReducer(
  MetricAlertsRequestTypes.GetAlertsListViewRequest,
  MetricAlertsRequestTypes.GetAlertsListViewSuccess,
  MetricAlertsRequestTypes.GetAlertsListViewFailure,
);

const errorReducer = createErrorReducer(
  MetricAlertsRequestTypes.GetAlertsListViewRequest,
  MetricAlertsRequestTypes.GetAlertsListViewSuccess,
  MetricAlertsRequestTypes.GetAlertsListViewFailure,
);

type MetricAlertsReducerType = {
  metadata: ReturnType<typeof metadataReducer>;
  activeAlerts: ReturnType<typeof activeAlertsReducer>;
  archivedAlerts: ReturnType<typeof archivedAlertsReducer>;
  byMetric: ReturnType<typeof byMetricReducer>;
  loading: ReturnType<typeof loadingReducer>;
  error: ReturnType<typeof errorReducer>;
};

export const metricAlertsReducer = combineReducers<MetricAlertsReducerType>({
  metadata: metadataReducer,
  activeAlerts: activeAlertsReducer,
  archivedAlerts: archivedAlertsReducer,
  byMetric: byMetricReducer,
  loading: loadingReducer,
  error: errorReducer,
});
